import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const STAGGER_GRIDS =
  ".card-grid, .tile-grid, .process, .area-grid, .gallery, .image-strip, .testimonial-grid, .faq";

const isInView = (el: Element) => {
  const r = el.getBoundingClientRect();
  return r.top < window.innerHeight && r.bottom > 0;
};

const setReveal = (el: Element, variant = "fade-up", i = 0) => {
  if (el.hasAttribute("data-reveal")) return;
  if (el.classList.contains("hero-content")) return;
  el.setAttribute("data-reveal", variant);
  if (i) (el as HTMLElement).style.setProperty("--reveal-i", String(i));
};

const tagAll = () => {
  document
    .querySelectorAll(".section > .container, .subhero > .container")
    .forEach((c) => {
      Array.from(c.children).forEach((child, i) => {
        if (child.matches(STAGGER_GRIDS)) return;
        if (child.classList.contains("split")) {
          Array.from(child.children).forEach((sc, si) =>
            setReveal(sc, si === 0 ? "fade-right" : "fade-left"),
          );
          return;
        }
        setReveal(child, "fade-up", Math.min(i, 3));
      });
    });

  document.querySelectorAll(STAGGER_GRIDS).forEach((grid) => {
    Array.from(grid.children).forEach((child, i) =>
      setReveal(child, "fade-up", Math.min(i, 8)),
    );
  });
};

export const useScrollReveal = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    tagAll();
    document.body.classList.add("motion-ready");

    if (reduced) {
      document
        .querySelectorAll("[data-reveal]")
        .forEach((el) => el.classList.add("is-visible"));
      return;
    }

    document.querySelectorAll("[data-reveal]").forEach((el) => {
      if (isInView(el)) el.classList.add("is-visible");
    });

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -6% 0px" },
    );

    document
      .querySelectorAll<HTMLElement>("[data-reveal]:not(.is-visible)")
      .forEach((el) => io.observe(el));

    const mo = new MutationObserver(() => {
      tagAll();
      document
        .querySelectorAll<HTMLElement>("[data-reveal]:not(.is-visible)")
        .forEach((el) => io.observe(el));
    });
    const main = document.getElementById("main-content");
    if (main) mo.observe(main, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, [pathname]);
};

export const useScrollProgress = () => {
  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const h = document.documentElement;
        const max = h.scrollHeight - h.clientHeight || 1;
        const pct = Math.min(100, Math.max(0, (window.scrollY / max) * 100));
        h.style.setProperty("--scroll-progress", `${pct}%`);
        ticking = false;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
};

export const useHeaderScrolled = (threshold = 80) => {
  useEffect(() => {
    const onScroll = () => {
      const scrolled = window.scrollY > threshold;
      document.body.classList.toggle("is-scrolled", scrolled);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);
};
